import React from 'react';
import { Box, IconButton, Paper, styled, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import {
    getDefaultSelectedModifierProductsPrice,
    getModifierOptionsForProduct,
    getNestedModifierProductsForProduct,
    IEnrichedProduct
} from 'components/menu/model/Menu';
import { useCurrencyString } from 'lib/useCurrencyString';
import { isDefined } from 'lib/typeInference';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store/store';

const StyledPaper = styled(Paper)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    width: '60%',
    maxWidth: '250px',
    flexShrink: 0,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    height: '100px',
    boxSizing: 'border-box'
}));

const StyledAddButton = styled(IconButton)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    position: 'absolute',
    bottom: theme.spacing(0.5),
    right: theme.spacing(0.5),
    zIndex: 10
}));

const StyledImage = styled('img')(() => ({
    width: '100%',
    height: '100%',
    objectFit: 'contain'
}));

interface IProps {
    product?: IEnrichedProduct;
    onAddToBasket?: (product: IEnrichedProduct) => void;
    onProductClick?: (product: IEnrichedProduct) => void;
}

export const BasketSuggestionItem = ({ product, onAddToBasket, onProductClick }: IProps) => {
    const getCurrencyString = useCurrencyString();
    const { menu } = useSelector((state: ApplicationState) => state.menu);

    const productTitle = React.useMemo(
        () => (isDefined(product?.productGroupName) ? product?.productGroupName : product?.title),
        [product?.productGroupName, product?.title]
    );
    const productImage = React.useMemo(
        () => product?.listingImageUrl || product?.imageUrl || false,
        [product?.imageUrl, product?.listingImageUrl]
    );

    const productPrice = React.useMemo(() => {
        let price = product?.price || 0;

        if (menu && product?.modifiers && product?.modifiers.length > 0) {
            const nestedModifierProducts = getNestedModifierProductsForProduct(menu, product.modifiers);
            const totalModifierProductsPrice =
                getDefaultSelectedModifierProductsPrice(nestedModifierProducts);
            const allOptionModifiers = getModifierOptionsForProduct(menu, product.id, product.modifiers);
            const optionModifiersPrice = allOptionModifiers
                .flatMap(modifier => modifier.options)
                .filter(option => option.selected)
                .map(option => option.price || 0)
                .reduce((acc, price) => acc + price, 0);
            price += optionModifiersPrice + totalModifierProductsPrice;
        }
        return Math.round(price * 100) / 100; // Prevent .00000001;
    }, [product?.modifiers, product?.price, product?.id, menu]);

    const handleProductClick = React.useCallback(() => {
        if (isDefined(product)) {
            onProductClick?.(product);
        }
    }, [product, onProductClick]);

    const handleAddToBasketClick = React.useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            if (isDefined(product)) {
                onAddToBasket?.(product);
            }
        },
        [product, onAddToBasket]
    );
    return (
        <StyledPaper elevation={0} key={product?.id} onClick={handleProductClick}>
            <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                padding={1}
                width="100%"
                position="relative"
            >
                <Box display="flex" flexDirection="column" justifyContent="space-between" gap={1}>
                    <Typography variant="body2">{productTitle}</Typography>
                    {!!product?.price && (
                        <Typography variant="caption">{getCurrencyString(productPrice)}</Typography>
                    )}
                </Box>
                {productImage && (
                    <Box>
                        <StyledImage src={productImage} alt={product?.title} />
                    </Box>
                )}
                <StyledAddButton color="primary" size="small" onClick={handleAddToBasketClick}>
                    <AddIcon fontSize="small" />
                </StyledAddButton>
            </Box>
        </StyledPaper>
    );
};
